import React from "react";
import { ChevronDown, Trash2, CheckSquare, Square, X } from "lucide-react";
import { STATUS_LABELS, STATUS_ORDER } from "./AdminDashboard.constants";

// ── Stats Bar ─────────────────────────────────────────────────────────────────

export function StatsBar({ analytics }) {
  const stats = [
    { label: "Total",    value: analytics.total || 0,                  color: "text-white" },
    { label: "Sitio",    value: analytics.site_generated || 0,         color: "text-[#FF4F00]" },
    { label: "Pagados",  value: analytics.paid || 0,                   color: "text-[#34D399]" },
    { label: "Conv.",    value: `${analytics.conversion_rate || 0}%`,  color: "text-[#A78BFA]" },
  ];

  return (
    <div className="px-4 grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 mb-4">
      {stats.map(({ label, value, color }) => (
        <div key={label} className="bg-white/5 border border-white/10 rounded-xl p-3 flex flex-col gap-0.5">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</span>
          <span className={`text-lg font-black font-heading ${color}`}>{value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Filter Tabs ───────────────────────────────────────────────────────────────

const FILTER_TABS = [{ key: "all", label: "Todos" }, ...STATUS_ORDER.map(s => ({ key: s, label: STATUS_LABELS[s] }))];

export function FilterTabs({ filter, setFilter }) {
  return (
    <div className="px-4 mb-3 flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
      {FILTER_TABS.map(({ key, label }) => (
        <button
          key={key}
          data-testid={`filter-${key}`}
          onClick={() => setFilter(key)}
          className={`flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
            filter === key
              ? "bg-[#ffc700] text-black border-[#ffc700]"
              : "bg-white/5 text-gray-400 border-white/10 hover:bg-white/10"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ── Bulk Action Bar ───────────────────────────────────────────────────────────

export function BulkActionBar({ selected, bulkLoading, showStatusDrop, setShowStatusDrop, bulkSetStatus, bulkDelete, onClear }) {
  return (
    <div
      data-testid="bulk-action-bar"
      className="mx-4 mb-3 bg-[#0A0A0A] text-white rounded-xl px-4 py-3 flex items-center gap-3 animate-fadeInUp"
    >
      <span className="text-sm font-semibold flex-1">{selected.size} seleccionada(s)</span>

      <div className="relative">
        <button
          data-testid="bulk-status-btn"
          onClick={() => setShowStatusDrop(s => !s)}
          disabled={bulkLoading}
          className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
        >
          Estado <ChevronDown size={12} />
        </button>
        {showStatusDrop && (
          <div className="absolute right-0 top-full mt-2 bg-white text-[#0A0A0A] rounded-xl shadow-xl py-1 min-w-[160px] z-50 border border-gray-100">
            {STATUS_ORDER.map(s => (
              <button
                key={s}
                onClick={() => bulkSetStatus(s)}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        data-testid="bulk-delete-btn"
        onClick={bulkDelete}
        disabled={bulkLoading}
        className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
      >
        {bulkLoading ? "..." : <><Trash2 size={12} /> Eliminar</>}
      </button>

      <button onClick={onClear} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
        <X size={14} />
      </button>
    </div>
  );
}

// ── Toolbar Row (SelectAll + Search + Sort) ───────────────────────────────────

const SORT_OPTIONS = [
  { value: "newest",   label: "Más nuevos primero" },
  { value: "oldest",   label: "Más viejos primero" },
  { value: "az",       label: "Alfabéticamente (A→Z)" },
  { value: "za",       label: "Alfabéticamente (Z→A)" },
  { value: "valor_desc", label: "Valor: mayor a menor" },
  { value: "valor_asc",  label: "Valor: menor a mayor" },
];

export function PipelineToolbar({
  allSelected, filteredCount, onSelectAll,
  search, onSearch,
  sortOrder, onSort,
}) {
  return (
    <div className="mb-3 flex items-center gap-3 flex-wrap">
      {/* Seleccionar todo */}
      <button
        data-testid="select-all-btn"
        onClick={onSelectAll}
        className="flex items-center gap-2 text-xs transition-colors flex-shrink-0"
        style={{ color: allSelected ? "#FFC107" : "#6b7280" }}
        onMouseEnter={e => e.currentTarget.style.color = "#fff"}
        onMouseLeave={e => e.currentTarget.style.color = allSelected ? "#FFC107" : "#6b7280"}
      >
        {allSelected
          ? <CheckSquare size={15} style={{ color: "#FFC107" }} />
          : <Square size={15} />
        }
        {allSelected ? "Deseleccionar todo" : `Seleccionar todo (${filteredCount})`}
      </button>

      {/* Search bar */}
      <div
        className="flex-1 min-w-[200px] flex items-center gap-2 rounded-xl px-3 py-2"
        style={{ background: "var(--ds-surface-card)", border: "1px solid rgba(255,255,255,0.08)" }}
      >
        <svg width="13" height="13" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          data-testid="pipeline-search"
          type="text"
          value={search}
          onChange={e => onSearch(e.target.value)}
          placeholder="Buscar contacto, email, categoría..."
          className="flex-1 bg-transparent text-sm text-white placeholder-neutral-500 outline-none border-none"
        />
        {search && (
          <button onClick={() => onSearch("")} style={{ color: "#6b7280" }}>
            <X size={13} />
          </button>
        )}
      </div>

      {/* Sort dropdown */}
      <div className="relative flex-shrink-0">
        <select
          data-testid="pipeline-sort"
          value={sortOrder}
          onChange={e => onSort(e.target.value)}
          className="appearance-none text-xs font-semibold rounded-xl pl-3 pr-8 py-2 outline-none cursor-pointer"
          style={{
            background: "var(--ds-surface-card)",
            border: "1px solid rgba(255,255,255,0.08)",
            color: sortOrder !== "newest" ? "#FFC107" : "#adaaaa",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 8px center",
            backgroundSize: "14px",
          }}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value} style={{ background: "#1a1a1a" }}>{o.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function SelectAllRow({ allSelected, filteredCount, onSelectAll }) {
  return (
    <div className="px-4 mb-2 flex items-center gap-2">
      <button
        data-testid="select-all-btn"
        onClick={onSelectAll}
        className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors"
      >
        {allSelected
          ? <CheckSquare size={15} className="text-[#FF4F00]" />
          : <Square size={15} className="text-gray-400" />}
        {allSelected ? "Deseleccionar todo" : `Seleccionar todo (${filteredCount})`}
      </button>
    </div>
  );
}
